// The dark sentence card: the sentence large (every word opens the Word Explorer), its transliteration, a Listen
// button and a word-by-word line of glosses. The glosses are word meanings, never a translation of the sentence.

import type { FC, ReactNode } from 'react'
import type { VakyaParse } from '@/prakrit/components/grammar/vakya/vakya-parse'

import { Fragment } from 'react'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { useWordPanel } from '@/prakrit/components/common/word-panel'
import { ListenButton } from '@/prakrit/components/grammar/vakya/listen-button'
import { glossOf } from '@/prakrit/components/grammar/vakya/vakya-labels'

interface SentenceCardProps {
    parse: VakyaParse
}

export const SentenceCard: FC<SentenceCardProps> = ({ parse }): ReactNode => {
    const { open } = useWordPanel()
    const sentence = `${parse.words.map(w => w.token).join(' ')}।`
    const roman = transliterate(sentence, { punctuation: true })

    return (
        <section aria-labelledby='vakya-sentence' className='rounded-3xl bg-foreground p-5 text-background shadow-card sm:p-8'>
            <h2 id='vakya-sentence' className='sr-only'>वाक्य · The sentence</h2>

            <div className='flex flex-col-reverse gap-4 sm:flex-row sm:items-start sm:justify-between'>
                <p lang='pra' className='pra pra-display min-w-0 break-words text-background!'>
                    {parse.words.map((w, i) => (
                        <Fragment key={`${w.token}-${i}`}>
                            {i > 0 && ' '}
                            {w.analyses.length > 0
                                ? <PrakritWord word={w.token} className='hover:bg-background/10 focus-visible:bg-background/10' />
                                : <span>{w.token}</span>}
                        </Fragment>
                    ))}
                    <span aria-hidden='true'>।</span>
                </p>
                <ListenButton text={sentence} tone='dark' className='self-end sm:self-start' />
            </div>

            <p lang='pra-Latn' className='mt-1 text-lg italic text-background/75 sm:text-xl'>{roman}</p>

            <p className='mt-6 text-xs font-semibold uppercase tracking-[0.12em] text-background/70'>
                <Bi hi='शब्दशः (पूरा अनुवाद नहीं)' en='Word by word (not a full translation)' layout='inline' />
            </p>
            <ul aria-label='शब्दशः अर्थ · Word-by-word meanings' className='mt-3 flex flex-wrap gap-2'>
                {parse.words.map((w, i) => {
                    const gloss = glossOf(w.chosen)
                    const known = w.analyses.length > 0

                    return (
                        <li key={`${w.token}-${i}`}>
                            {known ? (
                                <button
                                    type='button'
                                    onClick={() => open(w.token)}
                                    className='flex min-h-tap flex-col items-start rounded-2xl bg-background/10 px-3.5 py-2 text-left transition-colors hover:bg-background/20'
                                >
                                    <span lang='pra' className='pra pra-sm leading-tight text-saffron!'>{w.token}</span>
                                    <Bi
                                        hi={gloss.hi}
                                        en={gloss.en}
                                        className='text-sm leading-snug text-background/90'
                                        enClassName='text-background/70'
                                    />
                                </button>
                            ) : (
                                <span className='flex min-h-tap flex-col items-start rounded-2xl border border-dashed border-background/30 px-3.5 py-2'>
                                    <span lang='pra' className='pra pra-sm leading-tight text-background/80!'>{w.token}</span>
                                    <Bi
                                        hi={gloss.hi}
                                        en={gloss.en}
                                        className='text-sm leading-snug text-background/80'
                                        enClassName='text-background/60'
                                    />
                                </span>
                            )}
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
